import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../utils/api';

const ProfilePage = () => {
  const { t } = useTranslation();
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('info');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setError('Le mot de passe doit avoir au moins 8 caractères');
      return;
    }

    setLoading(true);
    try {
      await authAPI.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setSuccess('✓ Mot de passe modifié avec succès');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setError(err.response?.data?.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>👤 {t('nav.settings')} / Mon profil</h2>
          <p className="text-muted">{user?.firstName} {user?.lastName}</p>
        </div>
      </div>

      <div className="card" style={{ maxWidth: 800 }}>
        {/* Tabs */}
        <div style={{ borderBottom: '1px solid var(--gray-200)', padding: '0 24px', display: 'flex', gap: 32 }}>
          <button
            onClick={() => setActiveTab('info')}
            style={{
              background: 'none',
              border: 'none',
              padding: '16px 0',
              fontSize: 14,
              fontWeight: 600,
              color: activeTab === 'info' ? 'var(--primary)' : 'var(--gray-500)',
              borderBottom: activeTab === 'info' ? '2px solid var(--primary)' : '2px solid transparent',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            📋 Informations
          </button>
          <button
            onClick={() => setActiveTab('password')}
            style={{
              background: 'none',
              border: 'none',
              padding: '16px 0',
              fontSize: 14,
              fontWeight: 600,
              color: activeTab === 'password' ? 'var(--primary)' : 'var(--gray-500)',
              borderBottom: activeTab === 'password' ? '2px solid var(--primary)' : '2px solid transparent',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            🔐 {t('auth.changePassword')}
          </button>
        </div>

        <div className="card-body">
          {/* Info Tab */}
          {activeTab === 'info' && (
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 20 }}>Informations du compte</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('staff.firstName')}</label>
                  <input className="form-input" value={user?.firstName || ''} disabled />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('staff.lastName')}</label>
                  <input className="form-input" value={user?.lastName || ''} disabled />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('staff.email')}</label>
                  <input className="form-input" value={user?.email || ''} disabled />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('staff.role')}</label>
                  <input className="form-input" value={t(`roles.${user?.role}`) || ''} disabled />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">{t('staff.center')}</label>
                <input className="form-input" value={user?.centerName || '—'} disabled />
              </div>

              <div className="alert alert-info" style={{ marginTop: 24 }}>
                ℹ️ Pour modifier ces informations, contactez votre administrateur.
              </div>
            </div>
          )}

          {/* Password Tab */}
          {activeTab === 'password' && (
            <form onSubmit={handlePasswordChange}>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 20 }}>
                {t('auth.changePassword')}
              </h3>

              {error && <div className="alert alert-error">⚠ {error}</div>}
              {success && <div className="alert alert-success">{success}</div>}

              <div className="form-group">
                <label className="form-label">{t('auth.currentPassword')} *</label>
                <input
                  type="password"
                  className="form-input"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm(f => ({ ...f, currentPassword: e.target.value }))}
                  required
                  placeholder="••••••••"
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('auth.newPassword')} *</label>
                <input
                  type="password"
                  className="form-input"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm(f => ({ ...f, newPassword: e.target.value }))}
                  required
                  minLength={8}
                  placeholder="Minimum 8 caractères"
                />
                <p className="form-error" style={{ color: 'var(--gray-500)', fontSize: 12, marginTop: 4 }}>
                  Le mot de passe doit contenir au moins 8 caractères
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">{t('auth.confirmPassword')} *</label>
                <input
                  type="password"
                  className="form-input"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm(f => ({ ...f, confirmPassword: e.target.value }))}
                  required
                  minLength={8}
                  placeholder="Répétez le mot de passe"
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
                style={{ marginTop: 8 }}
              >
                {loading ? t('common.loading') : t('common.save')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
